import { useState, useEffect, useCallback } from "react";
import { ChevronUp } from "lucide-react";

const SCROLL_DURATION_MS = 450;

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

function scrollToTopAnimated(durationMs) {
  const startY = window.scrollY;
  if (startY <= 0) return;

  if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    window.scrollTo(0, 0);
    return;
  }

  const startTime = performance.now();

  function step(now) {
    const elapsed = now - startTime;
    const t = Math.min(elapsed / durationMs, 1);
    const eased = easeOutCubic(t);
    const y = startY * (1 - eased);
    window.scrollTo(0, y);
    if (t < 1) {
      requestAnimationFrame(step);
    }
  }

  requestAnimationFrame(step);
}

export default function FloatingScrollToTopButton() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let frame = 0;
    const update = () => {
      const next = window.scrollY > 280;
      setVisible((prev) => (prev === next ? prev : next));
    };
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        update();
      });
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  const handleClick = useCallback(() => {
    scrollToTopAnimated(SCROLL_DURATION_MS);
  }, []);

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label="Volver arriba"
      className={`fixed bottom-6 right-5 z-[60] flex h-12 w-12 items-center justify-center rounded-full bg-violet-fuerte text-white shadow-[0_8px_28px_-6px_rgba(74,58,138,0.45)] ring-1 ring-white/20 transition-[opacity,transform,box-shadow] duration-300 hover:bg-violet-oscuro hover:shadow-[0_12px_32px_-6px_rgba(74,58,138,0.5)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-fuerte md:bottom-8 md:right-8 md:h-14 md:w-14 ${
        visible ? "pointer-events-auto translate-y-0 opacity-100" : "pointer-events-none translate-y-2 opacity-0"
      }`}
    >
      <ChevronUp className="h-6 w-6 md:h-7 md:w-7" strokeWidth={2.5} aria-hidden />
    </button>
  );
}
